import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { departmentActionPlans, getActionStatusDetails } from '../data/departmentActionLogic';

export const DepartmentActionPlan = () => {
  const [selectedId, setSelectedId] = useState(departmentActionPlans[0].deptId);
  const currentPlan = useMemo(() => departmentActionPlans.find(p => p.deptId === selectedId), [selectedId]);

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Department Action Plan</h1>
          <p className="text-slate-500 mt-1">Konkrete tiltak og oppfølging for hver avdeling.</p>
        </div>
        <div className="flex gap-2">
          <select 
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500/20"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            {departmentActionPlans.map(p => <option key={p.deptId} value={p.deptId}>{p.name}</option>)}
          </select>
          <Button variant="primary" icon={<SafeIcon name="Download" />}>Eksporter Plan</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Action Table */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-none shadow-xl">
            <CardHeader 
              title="Tiltaksoversikt" 
              subtitle={`Operativ plan for ${currentPlan.name}`} 
              icon={<SafeIcon name="Target" className="text-indigo-600" />}
            />
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                    <tr>
                      <th className="px-6 py-4">Problem identifisert</th>
                      <th className="px-6 py-4">Anbefalt tiltak</th>
                      <th className="px-6 py-4">Ansvarlig / Frist</th>
                      <th className="px-6 py-4">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    <AnimatePresence mode="wait">
                      {currentPlan.actions.map((action) => {
                        const status = getActionStatusDetails(action.status);
                        return (
                          <motion.tr 
                            key={action.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="hover:bg-slate-50/50 transition-colors group"
                          >
                            <td className="px-6 py-5">
                              <p className="text-sm font-bold text-slate-900 leading-snug">{action.problem}</p>
                            </td>
                            <td className="px-6 py-5">
                              <p className="text-xs text-slate-600 font-medium">{action.intervention}</p>
                            </td>
                            <td className="px-6 py-5">
                              <div className="space-y-1">
                                <p className="text-xs font-bold text-slate-800">{action.responsible}</p>
                                <p className="text-[10px] text-indigo-600 font-black uppercase tracking-tighter">{action.timeline}</p>
                              </div>
                            </td>
                            <td className="px-6 py-5">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest bg-${status.color}-50 text-${status.color}-600 border border-${status.color}-100`}>
                                {status.label}
                              </span>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Follow-up Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-emerald-50 border-emerald-100">
              <CardContent className="p-6 flex gap-4 items-start">
                <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center text-emerald-600 shrink-0">
                  <SafeIcon name="ClipboardCheck" size={24} />
                </div>
                <div>
                  <h4 className="text-xs font-black text-emerald-900 uppercase tracking-widest mb-1">Neste Evaluering</h4>
                  <p className="text-lg font-black text-emerald-700">{currentPlan.actions[0].followUp}</p>
                  <p className="text-xs text-emerald-600/70 mt-1">Fullstendig re-kartlegging av avdelingen er planlagt for å måle effekt av tiltak.</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-indigo-50 border-indigo-100">
              <CardContent className="p-6 flex gap-4 items-start">
                <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center text-indigo-600 shrink-0">
                  <SafeIcon name="Users" size={24} />
                </div>
                <div>
                  <h4 className="text-xs font-black text-indigo-900 uppercase tracking-widest mb-1">Involvering</h4>
                  <p className="text-sm font-bold text-indigo-700">Verneombud & Tillitsvalgte</p>
                  <p className="text-xs text-indigo-600/70 mt-1">Planen er gjennomgått og forankret i AMU-møte den 12. mai.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Sidebar: Recommendations & Summary */}
        <div className="space-y-6">
          <Card className="bg-slate-900 border-none text-white">
            <CardHeader title="Management Recommendations" icon={<SafeIcon name="Award" className="text-indigo-400" />} />
            <CardContent className="space-y-6">
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5 italic text-sm text-slate-300 leading-relaxed">
                "{currentPlan.summary}"
              </div>
              <div className="space-y-4">
                <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Spesifikke råd til leder</h4>
                {currentPlan.managementRecs.map((rec, i) => (
                  <div key={i} className="flex gap-3 text-xs text-slate-300 items-start">
                    <SafeIcon name="Check" className="text-indigo-500 shrink-0 mt-0.5" />
                    <span>{rec}</span>
                  </div>
                ))}
              </div>
              <Button variant="secondary" className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20">
                Book Lederstøtte
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader title="Ressursbank" icon={<SafeIcon name="BookOpen" />} />
            <CardContent className="space-y-3">
              {[
                { title: 'Mal for prioriteringsmøte', icon: 'FileText' },
                { title: 'Guide: Psykologisk Trygghet', icon: 'Shield' },
                { title: 'Sjekkliste for AMU', icon: 'CheckSquare' }
              ].map((res, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-xl flex items-center gap-3 hover:bg-slate-100 transition-colors cursor-pointer group">
                  <SafeIcon name={res.icon} size={16} className="text-slate-400 group-hover:text-indigo-600" />
                  <span className="text-xs font-bold text-slate-700">{res.title}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};